import { MatchesViewer } from "components/MatchesViewer/MatchesViewer";
import { Panel } from "components/Panel/Panel";
import { SeasonTable } from "components/ResultTable/Season/SeasonTable";
import { TabBar } from "components/TabBar/TabBar";
import { Filter } from "model/Filter/Filter";
import {
  Season,
  SeasonPointsOverrides,
} from "model/Tournament/Segment/Season/Season";
import { GrowColumn } from "pages/styles";
import { useState } from "react";
import { selectAnyMatchHasDefinedRoster, selectMatches } from "selectors/Match";
import { matchHasDriverData } from "model/Match/Match";
import {
  getPreviousRoundsMatchIds,
  getRoundName,
} from "model/Tournament/Segment/RoundsSegmentGroup";
import {
  AltSeasonPointsRules,
  SeasonPointsRules,
} from "model/Tournament/Segment/Season/SeasonPointRules";
import { Theme } from "model/Theme";
import { TournamentDriverStats } from "components/TournamentDriverStats/TournamentDriverStats";
import { useAppSelector } from "app/hooks";

interface Props {
  season: Season;
  seasonPoints: SeasonPointsRules | undefined;
  seasonPointsOverrides: SeasonPointsOverrides | undefined;
  altSeasonPoints: AltSeasonPointsRules[] | undefined;
  tournamentId: string;
  theme?: Theme;
}

export const SeasonTabContent = ({
  season,
  seasonPoints,
  seasonPointsOverrides,
  altSeasonPoints,
  tournamentId,
  theme,
}: Props) => {
  const seasonOptions = season.rounds.map((r) =>
    getRoundName(r, season.roundPrefix)
  );

  const [selectedTab, setSelectedTab] = useState(seasonOptions[0]);
  const seasonFilter: Filter<string> = {
    options: seasonOptions,
    selectedValue: selectedTab,
    setSelectedValue: setSelectedTab,
  };

  const selectedRound = getRoundFromOption(season, seasonFilter.selectedValue);
  const showSeasonTable = useAppSelector((state) =>
    selectAnyMatchHasDefinedRoster(
      state,
      season.rounds.flatMap((r) => r.matchIds)
    )
  );

  const showPlaydayDriverStats = useAppSelector(
    (state) =>
      !!selectedRound &&
      tournamentId !== "Adlx2" &&
      tournamentId !== "NcsaNacx3" &&
      tournamentId !== "Alamo" &&
      tournamentId !== "Wmcx1" &&
      !tournamentId.includes("MajiMonday") &&
      !tournamentId.includes("Bymc") &&
      !tournamentId.includes("BonkCup") &&
      !tournamentId.includes("Cscx") &&
      selectMatches(state, selectedRound.matchIds).some(matchHasDriverData)
  );

  return (
    <GrowColumn>
      {showSeasonTable && (
        <Panel>
          <SeasonTable
            season={season}
            seasonPoints={seasonPoints}
            seasonPointsOverrides={seasonPointsOverrides}
            altSeasonPoints={altSeasonPoints}
            tournamentId={tournamentId}
            primaryColor={theme?.primary}
            secondaryColor={theme?.secondary}
          />
        </Panel>
      )}
      <Panel>
        <TabBar
          filter={seasonFilter}
          isSecondaryTabBar={true}
          primaryColor={theme?.primary}
          secondaryColor={theme?.secondary}
        />
        {!!selectedRound && (
          <MatchesViewer
            matchIds={selectedRound.matchIds}
            tournamentId={tournamentId}
            hoverColor={theme?.secondary}
          />
        )}
      </Panel>

      {!!selectedRound && showPlaydayDriverStats && (
        <Panel>
          <TournamentDriverStats
            matchIds={selectedRound.matchIds}
            tournamentId={tournamentId}
            priorMatchIds={getPreviousRoundsMatchIds(
              season.rounds,
              selectedRound.roundNum
            )}
            primaryColor={theme?.primary}
            secondaryColor={theme?.secondary}
            ratingType="Playday"
          />
        </Panel>
      )}
    </GrowColumn>
  );
};

const getRoundFromOption = (season: Season, option: string) => {
  const roundNumString = option.split(" ").at(-1);
  if (!roundNumString) {
    return undefined;
  }

  const roundNum = parseInt(roundNumString);
  return season.rounds.find((r) => r.roundNum === roundNum);
};
